import { type QuestStep, INITIAL_QUEST_STEPS } from './quest-schema.js';

export class QuestTracker {
  private quests: QuestStep[];
  private activeIndex: number = 0;
  private onCompletedListeners: ((quest: QuestStep) => void)[] = [];
  private onVictoryListeners: (() => void)[] = [];

  constructor() {
    this.quests = INITIAL_QUEST_STEPS.map((q) => ({
      ...q,
      currentCount: 0,
      completed: false
    }));
  }

  public getActiveQuest(): QuestStep | null {
    if (this.activeIndex < this.quests.length) {
      return this.quests[this.activeIndex];
    }
    return null;
  }

  public getAllQuests(): readonly QuestStep[] {
    return this.quests;
  }

  public isVictory(): boolean {
    return this.activeIndex >= this.quests.length;
  }

  public onQuestCompleted(cb: (quest: QuestStep) => void): () => void {
    this.onCompletedListeners.push(cb);
    return () => {
      this.onCompletedListeners = this.onCompletedListeners.filter((l) => l !== cb);
    };
  }

  public onVictory(cb: () => void): () => void {
    this.onVictoryListeners.push(cb);
    return () => {
      this.onVictoryListeners = this.onVictoryListeners.filter((l) => l !== cb);
    };
  }

  private advanceActiveQuest(): void {
    const current = this.quests[this.activeIndex];
    if (current && current.currentCount >= current.targetCount && !current.completed) {
      current.completed = true;
      for (const listener of this.onCompletedListeners) {
        listener(current);
      }
      this.activeIndex++;
      if (this.isVictory()) {
        for (const listener of this.onVictoryListeners) {
          listener();
        }
      }
    }
  }

  public recordMovement(distanceMeters: number): void {
    const current = this.getActiveQuest();
    if (current && current.id === 'quest_move') {
      current.currentCount += distanceMeters;
      this.advanceActiveQuest();
    }
  }

  public recordAbsorption(): void {
    const current = this.getActiveQuest();
    if (current && current.id === 'quest_absorb') {
      current.currentCount += 1;
      this.advanceActiveQuest();
    }
  }

  public recordMutation(): void {
    const current = this.getActiveQuest();
    if (current && current.id === 'quest_mutate') {
      current.currentCount += 1;
      this.advanceActiveQuest();
    }
  }

  public recordBuilding(): void {
    const current = this.getActiveQuest();
    if (current && current.id === 'quest_settle') {
      current.currentCount += 1;
      this.advanceActiveQuest();
    }
  }

  public recordTaming(): void {
    const current = this.getActiveQuest();
    if (current && current.id === 'quest_tame') {
      current.currentCount += 1;
      this.advanceActiveQuest();
    }
  }

  public recordBossDefeat(): void {
    const current = this.getActiveQuest();
    if (current && current.id === 'quest_boss') {
      current.currentCount += 1;
      this.advanceActiveQuest();
    }
  }
}
